import React from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

interface DevSecOpsTabsProps {
  activeTab?: string;
}

interface DevSecOpsTabConfig {
  id: string;
  label: string;
  path: string;
}

const tabs: DevSecOpsTabConfig[] = [
  { id: 'overview', label: 'Overview', path: '/devsecops/overview' },
  { id: 'pipeline', label: 'CI/CD Pipeline', path: '/devsecops/pipeline' },
  { id: 'metrics', label: 'Metrics Dashboard', path: '/devsecops/metrics' },
  { id: 'teams', label: 'Team Composition', path: '/devsecops/teams' },
  { id: 'transition', label: 'Transition & Support', path: '/devsecops/transition' },
  { id: 'deliverables', label: 'Deliverables & Schedules', path: '/devsecops/deliverables' },
  { id: 'quality', label: 'Quality Assurance', path: '/devsecops/quality' },
  { id: 'documentation', label: 'Documentation & Export', path: '/devsecops/documentation' }
];

const DevSecOpsTabs: React.FC<DevSecOpsTabsProps> = ({ activeTab }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentId = React.useMemo(() => {
    if (activeTab) {
      return activeTab;
    }
    const match = tabs.find((tab) => location.pathname.startsWith(tab.path));
    return match ? match.id : 'overview';
  }, [activeTab, location.pathname]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const target = tabs.find((tab) => tab.id === newValue);
    if (target) {
      navigate(target.path);
    }
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 1 }}>
      <Tabs
        value={currentId}
        onChange={handleChange}
        aria-label="DevSecOps navigation tabs"
        variant="standard"
        textColor="primary"
        indicatorColor="primary"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 0,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 48,
            minWidth: { xs: 140, md: 160 },
            alignItems: 'flex-end'
          }
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={tab.label}
            value={tab.id}
            aria-label={tab.label}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default DevSecOpsTabs;
